import React, { useEffect, useRef, useState } from 'react';
import { snapshots } from '../data';
import './Snapshots.css';

const AUTOPLAY_MS = 2500;
const DRAG_STEP = 60;

// The pictures form a loop: each card's steps from the open one, counted the short way round,
// so there are always pictures on both sides
function loopOffset(i, index, n) {
  let d = (((i - index) % n) + n) % n;
  if (d > n / 2) d -= n;
  return d;
}

export default function Snapshots() {
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(() => !window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [inView, setInView] = useState(false);
  const [dragging, setDragging] = useState(false);
  const panelRef = useRef(null);
  const drag = useRef(null);
  const lastOffsets = useRef(null);
  const count = snapshots.length;

  const step = by => setIndex(i => (i + by + count) % count);

  // Only runs while the section is on screen; any change of picture restarts the wait
  useEffect(() => {
    const io = new IntersectionObserver(([e]) => setInView(e.isIntersecting));
    io.observe(panelRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!playing || !inView) return undefined;
    const id = setTimeout(() => setIndex(i => (i + 1) % count), AUTOPLAY_MS);
    return () => clearTimeout(id);
  }, [index, playing, inView, count]);

  const onPointerDown = e => {
    drag.current = { x: e.clientX, moved: false };
  };

  const onPointerMove = e => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x;
    if (Math.abs(dx) < DRAG_STEP) return;
    if (!d.moved) {
      d.moved = true;
      setDragging(true);
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    d.x = e.clientX;
    step(dx < 0 ? 1 : -1);
  };

  const endDrag = () => {
    if (drag.current?.moved) {
      // The click that follows a drag must not also pick a card
      setTimeout(() => { drag.current = null; }, 0);
    } else {
      drag.current = null;
    }
    setDragging(false);
  };

  const offsets = snapshots.map((_, i) => loopOffset(i, index, count));
  // A picture coming round the back of the loop jumps to its new side and fades in, instead of flying across
  const jumped = offsets.map((o, i) => lastOffsets.current != null && Math.abs(o - lastOffsets.current[i]) > count / 2);

  useEffect(() => {
    lastOffsets.current = offsets;
  });

  return (
    <section id="snapshots" className="snapshots">
      <div ref={panelRef} className="snap-panel rv">
        <div className="snap-head">
          <h2 className="snap-title">Snapshots</h2>
          <p className="snap-caption">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
            </svg>
            <span>{snapshots[index].caption}</span>
            <span aria-hidden="true">·</span>
            <span>{index + 1} / {count}</span>
          </p>
        </div>

        <div
          className={`snap-reel ${dragging ? 'dragging' : ''}`}
          style={{ '--open-ratio': snapshots[index].ratio }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {snapshots.map((s, i) => {
            const offset = offsets[i];
            const distance = Math.abs(offset);
            return (
              <button
                key={s.caption}
                type="button"
                className="snap-card"
                style={{
                  '--ratio': s.ratio,
                  '--d': distance,
                  '--side': Math.sign(offset),
                  zIndex: 100 - distance,
                  transition: jumped[i] ? 'none' : undefined,
                  // Two identical fades, alternated so it replays on every jump
                  animation: jumped[i] ? `snapIn${index % 2} 0.6s ease` : undefined,
                  '--dim': distance ? 1 - Math.max(0.55, 0.9 - distance * 0.05) : 0,
                }}
                aria-label={s.caption}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => {
                  if (!drag.current?.moved) setIndex(i);
                }}
              >
                <img src={s.src} alt={s.alt} draggable="false" loading="lazy" decoding="async" />
              </button>
            );
          })}
        </div>

        <div className="snap-nav">
          <button type="button" className="snap-btn" onClick={() => step(-1)} aria-label="Previous picture">
            <svg viewBox="0 0 24 24" className="snap-line" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button type="button" className="snap-btn" onClick={() => setPlaying(p => !p)} aria-label={playing ? 'Pause autoplay' : 'Play autoplay'}>
            {playing ? (
              <svg viewBox="0 0 24 24" className="snap-solid" aria-hidden="true">
                <rect x="6" y="5" width="4" height="14" rx="1" />
                <rect x="14" y="5" width="4" height="14" rx="1" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" className="snap-solid" aria-hidden="true"><path d="M8 5v14l11-7z" /></svg>
            )}
          </button>
          <button type="button" className="snap-btn" onClick={() => step(1)} aria-label="Next picture">
            <svg viewBox="0 0 24 24" className="snap-line" aria-hidden="true"><path d="M9 18l6-6-6-6" /></svg>
          </button>
        </div>
      </div>
    </section>
  );
}
